import React, { useEffect, useState } from 'react';
import {
    Layout,
    Form,
    Input,
    Button,
    Card,
    Row,
    Col,
    Select,
    DatePicker,
    Switch,
    Space,
    message,
    Modal,
    Popconfirm,
    Menu,
    Typography
} from 'antd';
import {
    BankOutlined,
    HomeOutlined,
    BookOutlined,
    CarryOutOutlined,
    SafetyOutlined,
    PhoneOutlined,
    EditOutlined,
    DeleteOutlined,
    PlusOutlined,
    SaveOutlined,
    GithubOutlined,
    IdcardOutlined
} from '@ant-design/icons';
import dayjs from 'dayjs';
import * as adminactions from 'store/actions/AuthActions';

const { Option } = Select;
const { TextArea } = Input;
const { Sider, Content } = Layout;
const { Title, Text } = Typography;

const General = ({ isProfileLocked = false, employee = null }) => {
    const [form] = Form.useForm();
    const [loading, setLoading] = useState(false);
    const [activeSection, setActiveSection] = useState('basic');

    // Data states
    const [addresses, setAddresses] = useState([]);
    const [education, setEducation] = useState([]);
    const [experience, setExperience] = useState([]);
    const [emergencyContacts, setEmergencyContacts] = useState([]);

    // Modal states
    const [modalVisible, setModalVisible] = useState(false);
    const [modalType, setModalType] = useState('');
    const [editingRecord, setEditingRecord] = useState(null);
    const [modalForm] = Form.useForm();

    // Edit states for different sections
    const [isEditingFinancial, setIsEditingFinancial] = useState(false);


    useEffect(() => {
        if (employee) {
            loadEmployeeData();
        }
    }, [employee]);

    const loadEmployeeData = async () => {
        if (!employee?.id) return;

        try {
            const [addressesRes, educationRes, experienceRes, emergencyRes] = await Promise.all([
                adminactions.loadEmployeeAddresses(employee.id),
                adminactions.loadEmployeeEducation(employee.id),
                adminactions.loadEmployeeExperience(employee.id),
                adminactions.loadEmployeeEmergencyContacts(employee.id)
            ]);

            setAddresses(Array.isArray(addressesRes) ? addressesRes : []);
            setEducation(Array.isArray(educationRes) ? educationRes : []);
            setExperience(Array.isArray(experienceRes) ? experienceRes : []);
            setEmergencyContacts(Array.isArray(emergencyRes) ? emergencyRes : []);

            // Set form values
            form.setFieldsValue({
                employee_id: employee.employee_id,
                job_title: employee.job_title,
                department: employee.department,
                employment_type: employee.employment_type,
                date_of_joining: employee.date_of_joining ? dayjs(employee.date_of_joining) : null,
                date_of_birth: employee.date_of_birth ? dayjs(employee.date_of_birth) : null,
                pan_card: employee.pan_card,
                bank_account_number: employee.bank_account_number,
                bank_ifsc: employee.bank_ifsc,
                bank_name: employee.bank_name,
                bank_branch: employee.bank_branch,
                is_2fa_enabled: employee.user?.is_2fa_enabled || false,
                github_username: employee.github_username || '',
                gitlab_username: employee.gitlab_username || '',
            });
        } catch (error) {
            console.error('Error loading employee data:', error);
        }
    };


    const handleEditFinancial = () => {
        setIsEditingFinancial(true);
    };

    const handleSaveFinancial = async () => {
        if (isProfileLocked) {
            message.warning('Your profile is locked. You cannot make changes.');
            return;
        }

        try {
            const values = await form.validateFields([
                'pan_card',
                'bank_account_number',
                'bank_ifsc',
                'bank_name',
                'bank_branch'
            ]);

            setLoading(true);
            await adminactions.partialUpdateEmployee(values, employee.id);
            message.success('Financial information updated successfully!');
            setIsEditingFinancial(false);
        } catch (error) {
            message.error('Failed to update financial information');
            console.error('Update error:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleCancelFinancial = () => {
        // Reset form values to original
        form.setFieldsValue({
            pan_card: employee.pan_card,
            bank_account_number: employee.bank_account_number,
            bank_ifsc: employee.bank_ifsc,
            bank_name: employee.bank_name,
            bank_branch: employee.bank_branch,
        });
        setIsEditingFinancial(false);
    };

    const showModal = (type, record = null) => {
        setModalType(type);
        setEditingRecord(record);
        setModalVisible(true);
        if (record) {
            if (type === 'education' || type === 'experience') {
                modalForm.setFieldsValue({
                    ...record,
                    start_date: record.start_date ? dayjs(record.start_date) : null,
                    end_date: record.end_date ? dayjs(record.end_date) : null,
                });
            } else {
                modalForm.setFieldsValue(record);
            }
        } else {
            modalForm.resetFields();
        }
    };

    const handleModalCancel = () => {
        setModalVisible(false);
        setModalType('');
        setEditingRecord(null);
        modalForm.resetFields();
    };

    const handleDelete = async (type, recordId) => {
        if (isProfileLocked) {
            message.warning('Your profile is locked. You cannot make changes.');
            return;
        }

        try {
            let response;
            switch (type) {
                case 'address':
                    response = await adminactions.deleteEmployeeAddress(recordId);
                    break;
                case 'education':
                    response = await adminactions.deleteEmployeeEducation(recordId);
                    break;
                case 'experience':
                    response = await adminactions.deleteEmployeeExperience(recordId);
                    break;
                case 'emergency':
                    response = await adminactions.deleteEmployeeEmergencyContact(recordId);
                    break;
                default:
                    break;
            }

            if (response) {
                message.success(`${type.charAt(0).toUpperCase() + type.slice(1)} deleted successfully`);
                loadEmployeeData();
            }
        } catch (error) {
            message.error(`Failed to delete ${type}`);
            console.error('Delete error:', error);
        }
    };

    const handleModalSubmit = async (values) => {
        if (isProfileLocked) {
            message.warning('Your profile is locked. You cannot make changes.');
            return;
        }

        try {
            let response;
            const data = { ...values, employee: employee.id };

            if (modalType === 'education' || modalType === 'experience') {
                data.start_date = values.start_date ? values.start_date.format('YYYY-MM-DD') : null;
                data.end_date = values.end_date ? values.end_date.format('YYYY-MM-DD') : null;
            }

            switch (modalType) {
                case 'address':
                    if (editingRecord) {
                        response = await adminactions.updateEmployeeAddress(editingRecord.id, data);
                    } else {
                        response = await adminactions.addEmployeeAddress(data);
                    }
                    break;
                case 'education':
                    if (editingRecord) {
                        response = await adminactions.updateEmployeeEducation(editingRecord.id, data);
                    } else {
                        response = await adminactions.addEmployeeEducation(data);
                    }
                    break;
                case 'experience':
                    if (editingRecord) {
                        response = await adminactions.updateEmployeeExperience(editingRecord.id, data);
                    } else {
                        response = await adminactions.addEmployeeExperience(data);
                    }
                    break;
                case 'emergency':
                    if (editingRecord) {
                        response = await adminactions.updateEmployeeEmergencyContact(data, editingRecord.id);
                    } else {
                        response = await adminactions.addEmployeeEmergencyContact(data);
                    }
                    break;
                default:
                    break;
            }

            if (response && !response.message && !response.response) {
                message.success(`${modalType.charAt(0).toUpperCase() + modalType.slice(1)} ${editingRecord ? 'updated' : 'added'} successfully!`);
                handleModalCancel();
                loadEmployeeData();
            } else {
                // Extract validation errors from the response
                const errData = response?.response?.data;
                if (errData && typeof errData === 'object') {
                    const firstError = Object.entries(errData).map(([key, val]) => `${key}: ${Array.isArray(val) ? val.join(', ') : val}`).join('; ');
                    message.error(firstError || `Failed to save ${modalType}`);
                } else {
                    message.error(response?.message || `Failed to save ${modalType}`);
                }
            }
        } catch (error) {
            const errData = error?.response?.data;
            if (errData && typeof errData === 'object') {
                const firstError = Object.entries(errData).map(([key, val]) => `${key}: ${Array.isArray(val) ? val.join(', ') : val}`).join('; ');
                message.error(firstError || `Failed to save ${modalType}`);
            } else {
                message.error(`Failed to save ${modalType}`);
            }
            console.error('Save error:', error);
        }
    };

    const relationshipOptions = ['Father', 'Mother', 'Spouse', 'Sibling', 'Child', 'Friend', 'Other'];

    const menuItems = [
        { key: 'basic', icon: <IdcardOutlined />, label: 'Basic Information' },
        { key: 'employment', icon: <CarryOutOutlined />, label: 'Employment Details' },
        { key: 'security', icon: <SafetyOutlined />, label: 'Security Settings' },
        { key: 'addresses', icon: <HomeOutlined />, label: `Addresses (${addresses.length})` },
        { key: 'qualification', icon: <BookOutlined />, label: `Qualification (${education.length})` },
        { key: 'experience', icon: <CarryOutOutlined />, label: `Work Experience (${experience.length})` },
        { key: 'social', icon: <GithubOutlined />, label: 'Developer Profiles' },
        { key: 'financial', icon: <BankOutlined />, label: 'Financial Information' },
        { key: 'emergency', icon: <PhoneOutlined />, label: `Emergency Contacts (${emergencyContacts.length})` },
    ];

    const renderFieldRow = (label, value, span = 8) => (
        <Col xs={24} sm={12} md={span} key={label}>
            <div style={{ marginBottom: 16 }}>
                <Text type="secondary" style={{ display: 'block', marginBottom: 4 }}>{label}</Text>
                <Text>{value || '-'}</Text>
            </div>
        </Col>
    );

    const renderBasicInformation = () => {
        return (
            <Row gutter={[24, 16]}>
                <Col xs={24} sm={12} md={8}>
                    <div style={{ marginBottom: 16 }}>
                        <Text type="secondary" style={{ display: 'block', marginBottom: 4 }}>Employee ID</Text>
                        <Text strong>{employee?.employee_id || '-'}</Text>
                    </div>
                </Col>
                <Col xs={24} sm={12} md={8}>
                    <div style={{ marginBottom: 16 }}>
                        <Text type="secondary" style={{ display: 'block', marginBottom: 4 }}>Date of Birth</Text>
                        <Text strong>{employee?.date_of_birth || '-'}</Text>
                    </div>
                </Col>
                <Col xs={24} sm={12} md={8}>
                    <div style={{ marginBottom: 16 }}>
                        <Text type="secondary" style={{ display: 'block', marginBottom: 4 }}>User Account</Text>
                        <Text strong>{`${employee?.user?.first_name || ''} ${employee?.user?.last_name || ''} (${employee?.user?.email || ''})`}</Text>
                    </div>
                </Col>
                <Col xs={24} sm={12} md={8}>
                    <div style={{ marginBottom: 16 }}>
                        <Text type="secondary" style={{ display: 'block', marginBottom: 4 }}>Organization</Text>
                        <Text strong>{employee?.organization?.name || '-'}</Text>
                    </div>
                </Col>
            </Row>
        );
    };

    const renderEmploymentDetails = () => {
        return (
            <Row gutter={[24, 16]}>
                <Col xs={24} sm={12} md={8}>
                    <div style={{ marginBottom: 16 }}>
                        <Text type="secondary" style={{ display: 'block', marginBottom: 4 }}>Job Title</Text>
                        <Text strong>{employee?.job_title || '-'}</Text>
                    </div>
                </Col>
                <Col xs={24} sm={12} md={8}>
                    <div style={{ marginBottom: 16 }}>
                        <Text type="secondary" style={{ display: 'block', marginBottom: 4 }}>Department</Text>
                        <Text strong>{employee?.department || '-'}</Text>
                    </div>
                </Col>
                <Col xs={24} sm={12} md={8}>
                    <div style={{ marginBottom: 16 }}>
                        <Text type="secondary" style={{ display: 'block', marginBottom: 4 }}>Employment Type</Text>
                        <Text strong>{employee?.employment_type || '-'}</Text>
                    </div>
                </Col>
                <Col xs={24} sm={12} md={8}>
                    <div style={{ marginBottom: 16 }}>
                        <Text type="secondary" style={{ display: 'block', marginBottom: 4 }}>Date of Joining</Text>
                        <Text strong>{employee?.date_of_joining ? dayjs(employee.date_of_joining).format('YYYY-MM-DD') : '-'}</Text>
                    </div>
                </Col>
                <Col xs={24} sm={12} md={8}>
                    <div style={{ marginBottom: 16 }}>
                        <Text type="secondary" style={{ display: 'block', marginBottom: 4 }}>Report To</Text>
                        <Text strong>{employee?.report_to_name || '-'}</Text>
                    </div>
                </Col>
            </Row>
        );
    };

    const renderSecuritySettings = () => {
        return (
            <Row gutter={[24, 16]}>
                <Col xs={24} sm={12} md={8}>
                    <div style={{ marginBottom: 16 }}>
                        <Text type="secondary" style={{ display: 'block', marginBottom: 4 }}>Two-Factor Authentication</Text>
                        <Text strong>{employee?.user?.is_2fa_enabled ? 'Enabled' : 'Disabled'}</Text>
                        <div style={{ marginTop: 8 }}>
                            <Text type="secondary" style={{ fontSize: '12px' }}>
                                Contact your administrator to enable or disable 2FA
                            </Text>
                        </div>
                    </div>
                </Col>
            </Row>
        );
    };

    const renderAddresses = () => {
        return (
            <>
                {!isProfileLocked && (
                    <div style={{ marginBottom: 16, display: 'flex', justifyContent: 'flex-end' }}>
                        <Button
                            type="primary"
                            icon={<PlusOutlined />}
                            onClick={() => showModal('address')}
                        >
                            Add Address
                        </Button>
                    </div>
                )}
                {addresses.length === 0 ? (
                    <div style={{ textAlign: 'center', padding: '40px', color: '#999' }}>
                        No addresses added yet
                    </div>
                ) : (
                    addresses.map((address, index) => (
                        <Card
                            key={address.id}
                            style={{ marginBottom: 16 }}
                            title={`Address ${index + 1}`}
                            extra={
                                !isProfileLocked && (
                                    <Space>
                                        <Button
                                            icon={<EditOutlined />}
                                            size="small"
                                            onClick={() => showModal('address', address)}
                                        />
                                        <Popconfirm
                                            title="Sure to delete?"
                                            onConfirm={() => handleDelete('address', address.id)}
                                        >
                                            <Button
                                                icon={<DeleteOutlined />}
                                                size="small"
                                                danger
                                            />
                                        </Popconfirm>
                                    </Space>
                                )
                            }
                        >
                            <Row gutter={[24, 16]}>
                                {renderFieldRow('Address Line 1', address.address_line_1)}
                                {renderFieldRow('Address Line 2', address.address_line_2)}
                                {renderFieldRow('City', address.city)}
                                {renderFieldRow('State', address.state)}
                                {renderFieldRow('Postal Code', address.postal_code)}
                                {renderFieldRow('Country', address.country)}
                            </Row>
                        </Card>
                    ))
                )}
            </>
        );
    };

    const renderQualification = () => {
        return (
            <>
                {!isProfileLocked && (
                    <div style={{ marginBottom: 16, display: 'flex', justifyContent: 'flex-end' }}>
                        <Button
                            type="primary"
                            icon={<PlusOutlined />}
                            onClick={() => showModal('education')}
                        >
                            Add Education
                        </Button>
                    </div>
                )}
                {education.length === 0 ? (
                    <div style={{ textAlign: 'center', padding: '40px', color: '#999' }}>
                        No education records added yet
                    </div>
                ) : (
                    education.map((edu, index) => (
                        <Card
                            key={edu.id}
                            style={{ marginBottom: 16 }}
                            title={`${edu.degree} - ${edu.institution_name || edu.institution}`}
                            extra={
                                !isProfileLocked && (
                                    <Space>
                                        <Button
                                            icon={<EditOutlined />}
                                            size="small"
                                            onClick={() => showModal('education', edu)}
                                        />
                                        <Popconfirm
                                            title="Sure to delete?"
                                            onConfirm={() => handleDelete('education', edu.id)}
                                        >
                                            <Button
                                                icon={<DeleteOutlined />}
                                                size="small"
                                                danger
                                            />
                                        </Popconfirm>
                                    </Space>
                                )
                            }
                        >
                            <Row gutter={[24, 16]}>
                                {renderFieldRow('Field of Study', edu.field_of_study)}
                                {renderFieldRow('Grade', edu.grade)}
                                {renderFieldRow('Start Date', edu.start_date ? dayjs(edu.start_date).format('YYYY-MM-DD') : '-')}
                                {renderFieldRow('End Date', edu.end_date ? dayjs(edu.end_date).format('YYYY-MM-DD') : 'Present')}
                            </Row>
                        </Card>
                    ))
                )}
            </>
        );
    };

    const renderWorkExperience = () => {
        return (
            <>
                {!isProfileLocked && (
                    <div style={{ marginBottom: 16, display: 'flex', justifyContent: 'flex-end' }}>
                        <Button
                            type="primary"
                            icon={<PlusOutlined />}
                            onClick={() => showModal('experience')}
                        >
                            Add Experience
                        </Button>
                    </div>
                )}
                {experience.length === 0 ? (
                    <div style={{ textAlign: 'center', padding: '40px', color: '#999' }}>
                        No work experience added yet
                    </div>
                ) : (
                    experience.map((exp, index) => (
                        <Card
                            key={exp.id}
                            style={{ marginBottom: 16 }}
                            title={`${exp.job_title} at ${exp.company_name}`}
                            extra={
                                !isProfileLocked && (
                                    <Space>
                                        <Button
                                            icon={<EditOutlined />}
                                            size="small"
                                            onClick={() => showModal('experience', exp)}
                                        />
                                        <Popconfirm
                                            title="Sure to delete?"
                                            onConfirm={() => handleDelete('experience', exp.id)}
                                        >
                                            <Button
                                                icon={<DeleteOutlined />}
                                                size="small"
                                                danger
                                            />
                                        </Popconfirm>
                                    </Space>
                                )
                            }
                        >
                            <Row gutter={[24, 16]}>
                                {renderFieldRow('Start Date', exp.start_date ? dayjs(exp.start_date).format('YYYY-MM-DD') : '-')}
                                {renderFieldRow('End Date', exp.end_date ? dayjs(exp.end_date).format('YYYY-MM-DD') : 'Present')}
                                {renderFieldRow('Responsibilities', exp.responsibilities, 24)}
                            </Row>
                        </Card>
                    ))
                )}
            </>
        );
    };

    const renderSocialProfile = () => {
        return (
            <Row gutter={[24, 0]}>
                <Col xs={24} sm={12} md={8}>
                    <Form.Item label="GitHub Username" name="github_username">
                        <Input disabled={isProfileLocked} placeholder="Enter GitHub username" />
                    </Form.Item>
                </Col>
                <Col xs={24} sm={12} md={8}>
                    <Form.Item label="GitLab Username" name="gitlab_username">
                        <Input disabled={isProfileLocked} placeholder="Enter GitLab username" />
                    </Form.Item>
                </Col>
            </Row>
        );
    };

    const renderFinancialInformation = () => {
        return (
            <>
                {!isEditingFinancial && !isProfileLocked && (
                    <div style={{ marginBottom: 16, display: 'flex', justifyContent: 'flex-end' }}>
                        <Button type="primary" icon={<EditOutlined />} onClick={handleEditFinancial}>
                            Edit Financial Information
                        </Button>
                    </div>
                )}
                {isEditingFinancial && (
                    <div style={{ marginBottom: 16, display: 'flex', justifyContent: 'flex-end', gap: '8px' }}>
                        <Button onClick={handleCancelFinancial}>
                            Cancel
                        </Button>
                        <Button type="primary" icon={<SaveOutlined />} onClick={handleSaveFinancial} loading={loading}>
                            Save
                        </Button>
                    </div>
                )}
                <Row gutter={[24, 0]}>
                    <Col xs={24} sm={12} md={8}>
                        <Form.Item label="PAN Card" name="pan_card">
                            <Input disabled={!isEditingFinancial || isProfileLocked} placeholder="Enter PAN card number" />
                        </Form.Item>
                    </Col>
                    <Col xs={24} sm={12} md={8}>
                        <Form.Item label="Bank Account Number" name="bank_account_number">
                            <Input disabled={!isEditingFinancial || isProfileLocked} placeholder="Enter bank account number" />
                        </Form.Item>
                    </Col>
                    <Col xs={24} sm={12} md={8}>
                        <Form.Item label="Bank IFSC Code" name="bank_ifsc">
                            <Input disabled={!isEditingFinancial || isProfileLocked} placeholder="Enter IFSC code" />
                        </Form.Item>
                    </Col>
                    <Col xs={24} sm={12} md={8}>
                        <Form.Item label="Bank Name" name="bank_name">
                            <Input disabled={!isEditingFinancial || isProfileLocked} placeholder="Enter bank name" />
                        </Form.Item>
                    </Col>
                    <Col xs={24} sm={12} md={8}>
                        <Form.Item label="Bank Branch" name="bank_branch">
                            <Input disabled={!isEditingFinancial || isProfileLocked} placeholder="Enter bank branch" />
                        </Form.Item>
                    </Col>
                </Row>
            </>
        );
    };

    const renderEmergencyContacts = () => {
        return (
            <>
                {!isProfileLocked && (
                    <div style={{ marginBottom: 16, display: 'flex', justifyContent: 'flex-end' }}>
                        <Button
                            type="primary"
                            icon={<PlusOutlined />}
                            onClick={() => showModal('emergency')}
                        >
                            Add Emergency Contact
                        </Button>
                    </div>
                )}
                {emergencyContacts.length === 0 ? (
                    <div style={{ textAlign: 'center', padding: '40px', color: '#999' }}>
                        No emergency contacts added yet
                    </div>
                ) : (
                    emergencyContacts.map((contact, index) => (
                        <Card
                            key={contact.id}
                            style={{ marginBottom: 16 }}
                            title={`${contact.name}${contact.is_primary ? ' (Primary)' : ''}`}
                            extra={
                                !isProfileLocked && (
                                    <Space>
                                        <Button
                                            icon={<EditOutlined />}
                                            size="small"
                                            onClick={() => showModal('emergency', contact)}
                                        />
                                        <Popconfirm
                                            title="Sure to delete?"
                                            onConfirm={() => handleDelete('emergency', contact.id)}
                                        >
                                            <Button
                                                icon={<DeleteOutlined />}
                                                size="small"
                                                danger
                                            />
                                        </Popconfirm>
                                    </Space>
                                )
                            }
                        >
                            <Row gutter={[24, 16]}>
                                {renderFieldRow('Relationship', contact.relationship)}
                                {renderFieldRow('Phone Number', contact.phone_number)}
                                {renderFieldRow('Alternate Phone', contact.alternate_phone)}
                                {renderFieldRow('Email', contact.email)}
                                {renderFieldRow('Address', contact.address, 24)}
                            </Row>
                        </Card>
                    ))
                )}
            </>
        );
    };

    const renderModalContent = () => {
        switch (modalType) {
            case 'address':
                return (
                    <Row gutter={16}>
                        <Col xs={24}>
                            <Form.Item label="Address Line 1" name="address_line_1" rules={[{ required: true }]}>
                                <Input placeholder="Enter address line 1" />
                            </Form.Item>
                        </Col>
                        <Col xs={24}>
                            <Form.Item label="Address Line 2" name="address_line_2">
                                <Input placeholder="Enter address line 2" />
                            </Form.Item>
                        </Col>
                        <Col xs={24} sm={8}>
                            <Form.Item label="City" name="city" rules={[{ required: true }]}>
                                <Input placeholder="Enter city" />
                            </Form.Item>
                        </Col>
                        <Col xs={24} sm={8}>
                            <Form.Item label="State" name="state" rules={[{ required: true }]}>
                                <Input placeholder="Enter state" />
                            </Form.Item>
                        </Col>
                        <Col xs={24} sm={8}>
                            <Form.Item label="Postal Code" name="postal_code" rules={[{ required: true }]}>
                                <Input placeholder="Enter postal code" />
                            </Form.Item>
                        </Col>
                        <Col xs={24}>
                            <Form.Item label="Country" name="country" rules={[{ required: true }]}>
                                <Input placeholder="Enter country" />
                            </Form.Item>
                        </Col>
                    </Row>
                );
            case 'education':
                return (
                    <Row gutter={16}>
                        <Col xs={24} sm={12}>
                            <Form.Item label="Institution Name" name="institution_name" rules={[{ required: true }]}>
                                <Input placeholder="Enter institution name" />
                            </Form.Item>
                        </Col>
                        <Col xs={24} sm={12}>
                            <Form.Item label="Degree" name="degree" rules={[{ required: true }]}>
                                <Input placeholder="Enter degree" />
                            </Form.Item>
                        </Col>
                        <Col xs={24} sm={12}>
                            <Form.Item label="Field of Study" name="field_of_study" rules={[{ required: true, message: 'Please enter field of study' }]}>
                                <Input placeholder="Enter field of study" />
                            </Form.Item>
                        </Col>
                        <Col xs={24} sm={12}>
                            <Form.Item label="Grade/GPA" name="grade">
                                <Input placeholder="Enter grade" />
                            </Form.Item>
                        </Col>
                        <Col xs={24} sm={12}>
                            <Form.Item label="Start Date" name="start_date" rules={[{ required: true, message: 'Please select start date' }]}>
                                <DatePicker style={{ width: '100%' }} format="YYYY-MM-DD" placeholder="Select start date" />
                            </Form.Item>
                        </Col>
                        <Col xs={24} sm={12}>
                            <Form.Item label="End Date" name="end_date">
                                <DatePicker style={{ width: '100%' }} format="YYYY-MM-DD" placeholder="Select end date" />
                            </Form.Item>
                        </Col>
                    </Row>
                );
            case 'experience':
                return (
                    <Row gutter={16}>
                        <Col xs={24} sm={12}>
                            <Form.Item label="Company Name" name="company_name" rules={[{ required: true }]}>
                                <Input placeholder="Enter company name" />
                            </Form.Item>
                        </Col>
                        <Col xs={24} sm={12}>
                            <Form.Item label="Job Title" name="job_title" rules={[{ required: true }]}>
                                <Input placeholder="Enter job title" />
                            </Form.Item>
                        </Col>
                        <Col xs={24} sm={12}>
                            <Form.Item label="Start Date" name="start_date" rules={[{ required: true, message: 'Please select start date' }]}>
                                <DatePicker style={{ width: '100%' }} format="YYYY-MM-DD" placeholder="Select start date" />
                            </Form.Item>
                        </Col>
                        <Col xs={24} sm={12}>
                            <Form.Item label="End Date" name="end_date">
                                <DatePicker style={{ width: '100%' }} format="YYYY-MM-DD" placeholder="Select end date" />
                            </Form.Item>
                        </Col>
                        <Col xs={24}>
                            <Form.Item label="Responsibilities" name="responsibilities">
                                <TextArea rows={4} placeholder="Enter responsibilities" />
                            </Form.Item>
                        </Col>
                    </Row>
                );
            case 'emergency':
                return (
                    <Row gutter={16}>
                        <Col xs={24} sm={12}>
                            <Form.Item label="Full Name" name="name" rules={[{ required: true }]}>
                                <Input placeholder="Enter full name" />
                            </Form.Item>
                        </Col>
                        <Col xs={24} sm={12}>
                            <Form.Item label="Relationship" name="relationship" rules={[{ required: true }]}>
                                <Select placeholder="Select relationship">
                                    {relationshipOptions.map(rel => (
                                        <Option key={rel} value={rel}>{rel}</Option>
                                    ))}
                                </Select>
                            </Form.Item>
                        </Col>
                        <Col xs={24} sm={12}>
                            <Form.Item
                                label="Phone Number"
                                name="phone_number"
                                rules={[
                                    { required: true, message: 'Please enter phone number!' },
                                    { pattern: /^[0-9+\-\s()]{10,15}$/, message: 'Invalid phone number!' }
                                ]}
                            >
                                <Input placeholder="Enter phone number" />
                            </Form.Item>
                        </Col>
                        <Col xs={24} sm={12}>
                            <Form.Item
                                label="Alternate Phone"
                                name="alternate_phone"
                                rules={[{ pattern: /^[0-9+\-\s()]{10,15}$/, message: 'Invalid phone number!' }]}
                            >
                                <Input placeholder="Enter alternate phone" />
                            </Form.Item>
                        </Col>
                        <Col xs={24} sm={12}>
                            <Form.Item
                                label="Email"
                                name="email"
                                rules={[{ type: 'email', message: 'Invalid email format!' }]}
                            >
                                <Input placeholder="Enter email" />
                            </Form.Item>
                        </Col>
                        <Col xs={24} sm={12}>
                            <Form.Item label="Primary Contact" name="is_primary" valuePropName="checked">
                                <Switch />
                            </Form.Item>
                        </Col>
                        <Col xs={24}>
                            <Form.Item label="Address" name="address">
                                <TextArea rows={3} placeholder="Enter address" />
                            </Form.Item>
                        </Col>
                    </Row>
                );
            default:
                return null;
        }
    };

    const getSectionTitle = () => {
        const titles = {
            'basic': 'Basic Information',
            'employment': 'Employment Details',
            'security': 'Security Settings',
            'addresses': 'Addresses',
            'qualification': 'Qualification',
            'experience': 'Work Experience',
            'social': 'Developer Profiles',
            'financial': 'Financial Information',
            'emergency': 'Emergency Contacts'
        };
        return titles[activeSection] || 'Information';
    };

    const renderContent = () => {
        switch (activeSection) {
            case 'basic':
                return renderBasicInformation();
            case 'employment':
                return renderEmploymentDetails();
            case 'security':
                return renderSecuritySettings();
            case 'addresses':
                return renderAddresses();
            case 'qualification':
                return renderQualification();
            case 'experience':
                return renderWorkExperience();
            case 'social':
                return renderSocialProfile();
            case 'financial':
                return renderFinancialInformation();
            case 'emergency':
                return renderEmergencyContacts();
            default:
                return <Text>Section not found</Text>;
        }
    };

    return (
        <div style={{ height: 'calc(100vh - 280px)', padding: '20px' }}>
            <Card
                style={{ height: '100%', display: 'flex', flexDirection: 'column' }}
                bodyStyle={{ flex: 1, padding: 0, overflow: 'hidden', display: 'flex', flexDirection: 'column' }}
            >
                <Layout style={{ flex: 1, background: '#fff', overflow: 'hidden' }}>
                    {/* Sidebar */}
                    <Sider
                        width={240}
                        style={{
                            background: '#fff',
                            borderRight: '1px solid #f0f0f0',
                            overflow: 'auto'
                        }}
                    >
                        <style>
                            {`
                                .myprofile-sidebar-menu .ant-menu-item-selected {
                                    background-color: #1890ff !important;
                                    color: #fff !important;
                                }
                                .myprofile-sidebar-menu .ant-menu-item-selected .anticon {
                                    color: #fff !important;
                                }
                                .myprofile-sidebar-menu .ant-menu-item-selected::after {
                                    border-right-color: #1890ff !important;
                                }
                            `}
                        </style>
                        <Menu
                            mode="inline"
                            selectedKeys={[activeSection]}
                            onClick={({ key }) => setActiveSection(key)}
                            style={{ borderRight: 0 }}
                            items={menuItems}
                            className="myprofile-sidebar-menu"
                        />
                    </Sider>

                    {/* Content Area */}
                    <Content style={{ padding: '24px', overflow: 'auto', background: '#fff' }}>
                        <Title level={5} style={{ marginBottom: 24 }}>
                            {getSectionTitle()}
                        </Title>
                        <Form form={form} layout="vertical">
                            {renderContent()}
                        </Form>
                    </Content>
                </Layout>
            </Card>

            {/* Modal for adding/editing records */}
            <Modal
                title={`${editingRecord ? 'Edit' : 'Add'} ${modalType.charAt(0).toUpperCase() + modalType.slice(1)}`}
                open={modalVisible}
                onCancel={handleModalCancel}
                footer={null}
                width={800}
            >
                <Form form={modalForm} layout="vertical" onFinish={handleModalSubmit}>
                    {renderModalContent()}
                    <div style={{ textAlign: 'right', marginTop: 16 }}>
                        <Space>
                            <Button onClick={handleModalCancel}>Cancel</Button>
                            <Button type="primary" htmlType="submit">
                                {editingRecord ? 'Update' : 'Add'} {modalType.charAt(0).toUpperCase() + modalType.slice(1)}
                            </Button>
                        </Space>
                    </div>
                </Form>
            </Modal>
        </div>
    );
};

export default General;
